import React, { useCallback, useLayoutEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { editorToolbarPluginKey } from '@atlaskit/editor-plugin-toolbar/plugin-key';
import { ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { isRemixInlineDropdownVariant, REMIX_TOOLBAR_POPUP_OFFSET } from '../remix-config';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';
import {
	getEditorRemixLifecyclePresentation,
	subscribeToEditorRemixLifecyclePresentation,
} from '../utils/editor-remix-callback-registry';
import { hasAiEditInProgressState } from '../utils/is-ai-edit-in-progress';

const testId = 'editor-toolbar__remix-button';

interface RemixButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRemixHidden?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}

export const RemixButton = ({
	api,
	isDisabled,
	isRemixHidden,
	remixDisabledReason,
}: RemixButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLegacyRemixing, isUpdate, aiEditInProgress, areAiEntryPointsDisabled } =
		useSharedPluginStateWithSelector(api, ['malleableUi', 'aiExperience'], (states) => ({
			isLegacyRemixing:
				states.malleableUiState?.isRemixModalOpen ||
				false ||
				states.malleableUiState?.isThinking ||
				false,
			isUpdate: states.malleableUiState?.isUpdate ?? false,
			areAiEntryPointsDisabled: states.aiExperienceState?.areAiEntryPointsDisabled ?? false,
			aiEditInProgress: hasAiEditInProgressState(states.aiExperienceState),
		}));

	// Ownership lives in a synchronous registry rather than plugin state, so subscribe to keep the
	// button's disabled state and tooltip in step with the flow starting and ending.
	const [, refreshMachinePresentation] = useState(0);
	useLayoutEffect(
		() =>
			subscribeToEditorRemixLifecyclePresentation(api, () =>
				refreshMachinePresentation((value) => value + 1),
			),
		[api],
	);
	// A machine-owned flow is a Remix in progress even though the projection reports `!isThinking`
	// once the early streaming whiteboard lands. Without this the button reads as disabled with no
	// reason, and the tooltip helper's default renders the archived copy on an ordinary page.
	// Only `inserting` extends "in progress". Ownership stays active after the flow completes, so
	// keying off it would leave this button disabled for the rest of the editor session.
	const machinePresentation = getEditorRemixLifecyclePresentation(api);
	const isRemixing = isLegacyRemixing || machinePresentation.phase === 'inserting';
	// Active lifecycle authority is the treatment-owned gate, so the toolbar need not re-evaluate
	// the editor experiment while preserving legacy and Batch 5 classification paths.
	const isMachineOwnedEditInProgress =
		machinePresentation.authority === 'active' && isUpdate && isRemixing;

	const effectiveDisabled =
		isDisabled ||
		areAiEntryPointsDisabled ||
		(aiEditInProgress && isExperimentEnabled('cc_maui_polish_changes_batch_5'));

	// Only the inline-dropdown cohort renders the anchored picker; every other assignment
	// keeps the central modal experience.
	const isInlineDropdownVariant = isRemixInlineDropdownVariant();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const openRemix = useCallback(
		(preselection?: RemixOptionSelectionParameters) => {
			const openModal = api?.malleableUi.commands.openRemixModal({
				metadata: {
					interactionSource: 'remixModalFromToolbar',
					...(preselection && {
						preselectedType: preselection.type,
						preselectedSubType: preselection.subType,
						...(preselection.userPrompt && {
							preselectedUserPrompt: preselection.userPrompt,
						}),
					}),
				},
				source: 'selectionToolbar',
			});
			if (!openModal) {
				return;
			}
			const featureArea = getRemixFeatureArea({});
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'selectionToolbar',
					product: 'confluence',
					...(featureArea !== undefined && { featureArea }),
				},
			});
			startRemixCreateExperience({
				experienceTracker,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'selectionToolbar',
					product: 'confluence',
				},
			});
			api?.core.actions.execute(({ tr }) => {
				openModal({ tr });
				tr.setMeta(editorToolbarPluginKey, { shouldShowToolbar: false });
				return tr;
			});
		},
		[api, experienceTracker],
	);

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'remixModalFromToolbar',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				attributes: {
					entryPoint: 'editor',
				},
			},
		}).fire();

		expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		if (isInlineDropdownVariant) {
			setIsDropdownOpen((open) => !open);
			return;
		}
		openRemix();
	}, [createAnalyticsEvent, effectiveDisabled, isInlineDropdownVariant, isRemixing, openRemix]);

	const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

	const handleOptionSelect = useCallback(
		(selection: RemixOptionSelectionParameters) => {
			closeDropdown();
			openRemix(selection);
		},
		[closeDropdown, openRemix],
	);

	// Evaluate the visibility gate first and bail out before touching cc_maui_create_keyword, so we
	// don't fire a cc_maui_create_keyword exposure for users who never see the Remix button.
	if (isRemixHidden || !expValEquals('cc-maui-experiment', 'isEnabled', true)) {
		return null;
	}

	// cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create". Evaluated here
	// (after the visibility gate) so the exposure only fires when the button is actually rendered.
	const isCreateKeywordEnabled = expValEquals('cc_maui_create_keyword', 'isEnabled', true);
	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);

	let tooltipContent: string;
	if (
		isMachineOwnedEditInProgress ||
		(aiEditInProgress && isExperimentEnabled('cc_maui_polish_changes_batch_5'))
	) {
		tooltipContent = formatMessage(messages.editInProgressTooltip);
	} else if (isRemixing) {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled ? messages.createInProgressTooltip : messages.remixInProgressTooltip,
		);
	} else if (effectiveDisabled) {
		tooltipContent = getRemixDisabledTooltip(formatMessage, remixDisabledReason);
	} else if (fg('cc_maui_updated_remix_tooltip_text')) {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled
				? messages.createBlockTooltipUpdated
				: messages.remixToolbarTooltipUpdated,
		);
	} else {
		tooltipContent = formatMessage(
			isCreateKeywordEnabled ? messages.createToolbarTooltip : messages.remixToolbarTooltip,
		);
	}

	const renderButton = (triggerProps?: TriggerProps) => (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				{...triggerProps}
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={testId}
				onClick={handleClick}
				isSelected={isInlineDropdownVariant ? isDropdownOpen : undefined}
				isDisabled={effectiveDisabled || isRemixing}
			>
				{formatMessage(
					isCreateKeywordEnabled ? messages.createToolbarLabel : messages.remixToolbarLabel,
				)}
			</ToolbarButton>
		</ToolbarTooltip>
	);

	if (!isInlineDropdownVariant) {
		return renderButton();
	}

	// The button itself is the picker: the anchored dropdown hangs off it and hands the
	// selection to the remix flow, which is mounted separately and driven by plugin state.
	return (
		<RemixContainer
			presentation="popup"
			trigger={renderButton}
			popupPlacement="bottom-start"
			popupOffset={REMIX_TOOLBAR_POPUP_OFFSET}
			isPopupOpen={isDropdownOpen}
			isThinking={false}
			onClose={closeDropdown}
			onOptionSelect={handleOptionSelect}
		/>
	);
};
